import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { post } from "../services/api";

interface SiteConfig {
  site_name: string | null;
  photo_required: boolean;
  gps_required: boolean;
}

interface ConfigItem {
  id: number;
  item_type: 'checklist' | 'document_upload' | 'youtube' | 'image' | 'document_link';
  action_scope: string;
  label: string;
  description?: string;
  resource_url?: string;
  is_required?: number;
  sort_order: number;
}

const CleaningLog: React.FC = () => {
  // Parse URL query parameters
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("client_id") || "";
  const siteId = params.get("site_id") || "";
  const action = (params.get("action") || "IN").toUpperCase();

  const [workerIdentifier, setWorkerIdentifier] = useState("");
  const [gpsLat, setGpsLat] = useState<number | null>(null);
  const [gpsLong, setGpsLong] = useState<number | null>(null);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [gpsStatus, setGpsStatus] = useState("Acquiring location...");
  const [gpsReady, setGpsReady] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [configResponses, setConfigResponses] = useState<Record<string, any>>({});
  const [posting, setPosting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<any>(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<any>({});

  // Config from API
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [configItems, setConfigItems] = useState<ConfigItem[]>([]);
  const [configLoading, setConfigLoading] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Fetch site config on mount
  useEffect(() => {
    if (!clientId || !siteId) return;

    const fetchConfig = async () => {
      try {
        const response: any = await post("survey/postServices", {
          type: "siteAccessConfig",
          client_id: Number(clientId),
          site_id: Number(siteId),
          action: action as "IN" | "OUT",
        });
        setSiteConfig(response.payload.site);
        setConfigItems(response.payload.items || []);
      } catch (e) {
        console.log("Config fetch error:", e);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, [clientId, siteId, action]);

  // Request geolocation on mount (only if GPS required)
  useEffect(() => {
    // Wait for config to load before deciding
    if (configLoading) return;

    if (!siteConfig?.gps_required) {
      setGpsReady(true);
      setGpsStatus("Location not required");
      return;
    }

    if (!("geolocation" in navigator)) {
      setGpsStatus("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLat(position.coords.latitude);
        setGpsLong(position.coords.longitude);
        setGpsAccuracy(Math.round(position.coords.accuracy));
        setGpsReady(true);
        setGpsStatus("Location verified");
      },
      (err) => {
        setGpsStatus(
          "Location access denied. Please enable location in your browser settings."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [configLoading, siteConfig?.gps_required]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setFieldErrors((prev: any) => ({ ...prev, photo: "" }));
    }
  };

  const handleConfigToggle = (itemId: number) => {
    const key = `config_item_${itemId}`;
    setConfigResponses((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDocUpload = async (itemId: number, file: File) => {
    const key = `config_item_${itemId}`;
    try {
      const urlResponse: any = await post("survey/postServices", {
        type: "siteAccessPhotoUrl",
        client_id: Number(clientId),
        site_id: Number(siteId),
        file_name: file.name,
      });

      const { signedUrl, fileUrl } = urlResponse.payload;

      await axios.put(signedUrl, file, {
        headers: { "Content-Type": "" },
      });

      setConfigResponses((prev) => ({ ...prev, [key]: fileUrl }));
      setFieldErrors((prev: any) => ({ ...prev, [key]: "" }));
    } catch (e) {
      console.log("Document upload error:", e);
      setFieldErrors((prev: any) => ({
        ...prev,
        [key]: "Upload failed. Please try again.",
      }));
    }
  };

  const validate = (): boolean => {
    const errors: any = {};

    if (!clientId || !siteId) {
      setError("Invalid QR code. Missing client or site information.");
      return false;
    }

    if (!workerIdentifier.trim()) {
      errors.worker = "Employee ID is required.";
    }

    if (siteConfig?.photo_required && !photoFile) {
      errors.photo = "Photo is required.";
    }

    if (siteConfig?.gps_required && !gpsReady) {
      errors.gps = "Location must be verified before submitting.";
    }

    // Validate required config items
    for (const item of configItems) {
      if (!item.is_required) continue;
      const key = `config_item_${item.id}`;
      if (!configResponses[key]) {
        errors[key] = `${item.label} is required.`;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile) return null;

    try {
      const urlResponse: any = await post("survey/postServices", {
        type: "siteAccessPhotoUrl",
        client_id: Number(clientId),
        site_id: Number(siteId),
        file_name: photoFile.name,
      });

      const { signedUrl, fileUrl } = urlResponse.payload;

      await axios.put(signedUrl, photoFile, {
        headers: { "Content-Type": "" },
      });

      return fileUrl;
    } catch (e) {
      console.log("Photo upload error:", e);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validate()) return;

    setPosting(true);

    try {
      // Upload photo if required
      let photoUrl: string | null = null;
      if (siteConfig?.photo_required) {
        photoUrl = await uploadPhoto();
        if (!photoUrl) {
          setError("Failed to upload photo. Please try again.");
          setPosting(false);
          return;
        }
      }

      const payload: any = {
        type: "siteAccessRecord",
        client_id: Number(clientId),
        site_id: Number(siteId),
        worker_identifier: workerIdentifier.trim(),
        event_type: action,
        gps_lat: gpsLat,
        gps_long: gpsLong,
        gps_accuracy_m: gpsAccuracy,
        photo_url: photoUrl,
      };

      // Include config responses as checklist_data
      if (Object.keys(configResponses).length > 0) {
        payload.checklist_data = configResponses;
      }

      const response: any = await post("survey/postServices", payload);

      setSubmitResult(response.payload);
      setSubmitted(true);
    } catch (err) {
      console.log("Submit error:", err);
      setError("Submission failed. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  // Render a single config item by type
  const renderConfigItem = (item: ConfigItem) => {
    const key = `config_item_${item.id}`;

    switch (item.item_type) {
      case "checklist":
        return (
          <label style={styles.checklistItem} key={item.id}>
            <input
              type="checkbox"
              checked={!!configResponses[key]}
              onChange={() => handleConfigToggle(item.id)}
              style={styles.checkbox}
            />
            <div>
              <span>{item.label}</span>
              {item.description && (
                <small style={{ display: "block", color: "#888", marginTop: 2 }}>
                  {item.description}
                </small>
              )}
            </div>
          </label>
        );

      case "document_upload":
        return (
          <div key={item.id} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <span style={{ fontSize: 15 }}>{item.label}</span>
                {item.description && (
                  <small style={{ display: "block", color: "#888", marginTop: 2 }}>
                    {item.description}
                  </small>
                )}
              </div>
              <button
                type="button"
                style={styles.uploadBtn}
                onClick={() => docInputRefs.current[key]?.click()}
              >
                {configResponses[key] ? "Uploaded" : "Upload"}
              </button>
            </div>
            <input
              ref={(el) => { docInputRefs.current[key] = el; }}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleDocUpload(item.id, file);
              }}
              style={{ display: "none" }}
            />
            {fieldErrors[key] && (
              <small style={styles.errorText}>{fieldErrors[key]}</small>
            )}
          </div>
        );

      case "youtube":
        return (
          <div key={item.id} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{item.label}</span>
            {item.resource_url && (
              <div style={{ marginTop: 8, borderRadius: 8, overflow: "hidden" }}>
                <iframe
                  width="100%"
                  height="200"
                  src={item.resource_url}
                  title={item.label}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {!!item.is_required && (
              <label style={{ display: "flex", alignItems: "center", marginTop: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!configResponses[key]}
                  onChange={() => handleConfigToggle(item.id)}
                  style={styles.checkbox}
                />
                <span>I have reviewed this</span>
              </label>
            )}
            {fieldErrors[key] && (
              <small style={styles.errorText}>{fieldErrors[key]}</small>
            )}
          </div>
        );

      case "image":
        return (
          <div key={item.id} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{item.label}</span>
            {item.resource_url && (
              <img
                src={item.resource_url}
                alt={item.label}
                style={{ width: "100%", borderRadius: 8, marginTop: 8 }}
              />
            )}
            {item.description && (
              <small style={{ display: "block", color: "#888", marginTop: 4 }}>
                {item.description}
              </small>
            )}
            {!!item.is_required && (
              <label style={{ display: "flex", alignItems: "center", marginTop: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!configResponses[key]}
                  onChange={() => handleConfigToggle(item.id)}
                  style={styles.checkbox}
                />
                <span>I have reviewed this</span>
              </label>
            )}
            {fieldErrors[key] && (
              <small style={styles.errorText}>{fieldErrors[key]}</small>
            )}
          </div>
        );

      case "document_link":
        return (
          <div key={item.id} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <div>
              <span style={{ fontSize: 15 }}>{item.label}</span>
              {item.description && (
                <small style={{ display: "block", color: "#888", marginTop: 2 }}>
                  {item.description}
                </small>
              )}
            </div>
            {item.resource_url && (
              <a
                href={item.resource_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0cb99d", fontSize: 14, display: "inline-block", marginTop: 4 }}
              >
                Open Document
              </a>
            )}
            {!!item.is_required && (
              <label style={{ display: "flex", alignItems: "center", marginTop: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!configResponses[key]}
                  onChange={() => handleConfigToggle(item.id)}
                  style={styles.checkbox}
                />
                <span>I have reviewed this</span>
              </label>
            )}
            {fieldErrors[key] && (
              <small style={styles.errorText}>{fieldErrors[key]}</small>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Missing params guard
  if (!clientId || !siteId) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.errorHeading}>Invalid Link</h2>
          <p style={styles.text}>
            This link is missing required information. Please scan the QR code
            at your site to clock in or out.
          </p>
        </div>
      </div>
    );
  }

  // Loading config
  if (configLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ ...styles.text, fontSize: 16 }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Site not configured
  if (!configLoading && !siteConfig) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.errorHeading}>Site Not Configured</h2>
          <p style={styles.text}>
            This site has not been set up for clock in/out yet. Please contact
            your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Success view
  if (submitted) {
    const isViolation = submitResult?.is_geofence_violation;
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            {isViolation ? "\u26A0\uFE0F" : "\u2705"}
          </div>
          <h2 style={styles.heading}>
            {action === "IN" ? "Shift Started" : "Shift Ended"}
          </h2>
          <p style={styles.text}>
            Your {action === "IN" ? "clock in" : "clock out"} has been
            recorded.
          </p>
          {isViolation && (
            <div style={styles.warningBox}>
              <strong>Geofence Alert:</strong> You appear to be{" "}
              {submitResult.distance_from_site_m}m from the site. This has been
              flagged for review.
            </div>
          )}
          <p style={styles.timestamp}>
            {new Date().toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  const isSubmitDisabled = posting || (siteConfig?.gps_required && !gpsReady);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.headerBar}>
          <h2 style={styles.heading}>
            {action === "IN" ? "Start Shift" : "End Shift - Report"}
          </h2>
          <p style={styles.subheading}>
            {siteConfig?.site_name || `Site #${siteId}`} | Client #{clientId}
          </p>
        </div>

        {/* GPS Status — only if GPS required */}
        {siteConfig?.gps_required && (
          <>
            <div
              style={{
                ...styles.gpsStatusBar,
                backgroundColor: gpsReady ? "#e6f9f0" : "#fff3cd",
                borderColor: gpsReady ? "#0cb99d" : "#ffc107",
              }}
            >
              <span>{gpsReady ? "\u2705" : "\u23F3"}</span>
              <span style={{ marginLeft: 8 }}>{gpsStatus}</span>
              {gpsReady && gpsAccuracy && (
                <span style={styles.accuracyBadge}>
                  {"\u00B1"}{gpsAccuracy}m
                </span>
              )}
            </div>

            {fieldErrors.gps && (
              <small style={styles.errorText}>{fieldErrors.gps}</small>
            )}
          </>
        )}

        <form onSubmit={handleSubmit}>
          {/* Employee ID */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Employee ID</label>
            <input
              type="text"
              style={styles.input}
              placeholder="Enter your Employee ID"
              value={workerIdentifier}
              onChange={(e) => {
                setWorkerIdentifier(e.target.value);
                setFieldErrors((prev: any) => ({ ...prev, worker: "" }));
              }}
            />
            {fieldErrors.worker && (
              <small style={styles.errorText}>{fieldErrors.worker}</small>
            )}
          </div>

          {/* Dynamic Config Items */}
          {configItems.length > 0 && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {action === "OUT" ? "End-of-Shift Checklist" : "Requirements"}
              </label>
              <div style={styles.checklistContainer}>
                {configItems.map(renderConfigItem)}
              </div>
            </div>
          )}

          {/* Photo Capture — only if photo required */}
          {siteConfig?.photo_required && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {action === "IN" ? "Arrival Selfie" : "Site Condition Photo"}
              </label>
              <div
                style={styles.photoArea}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={styles.photoPreview}
                  />
                ) : (
                  <div style={styles.photoPlaceholder}>
                    <span style={{ fontSize: 32 }}>{"\uD83D\uDCF7"}</span>
                    <span>Tap to take photo</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture={action === "IN" ? "user" : "environment"}
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
              {fieldErrors.photo && (
                <small style={styles.errorText}>{fieldErrors.photo}</small>
              )}
            </div>
          )}

          {/* Error */}
          {error && <div style={styles.errorBox}>{error}</div>}

          {/* Submit */}
          <button
            type="submit"
            disabled={!!isSubmitDisabled}
            style={{
              ...styles.submitBtn,
              opacity: isSubmitDisabled ? 0.6 : 1,
            }}
          >
            {posting
              ? "Submitting..."
              : siteConfig?.gps_required && !gpsReady
              ? "Locating..."
              : action === "IN"
              ? "Clock In"
              : "Clock Out"}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "16px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    width: "100%",
    maxWidth: 480,
    padding: 24,
    marginTop: 16,
  },
  headerBar: {
    marginBottom: 20,
    borderBottom: "2px solid #0cb99d",
    paddingBottom: 12,
  },
  heading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#333",
  },
  errorHeading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#dc3545",
  },
  subheading: {
    margin: "4px 0 0",
    fontSize: 14,
    color: "#888",
  },
  gpsStatusBar: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid",
    marginBottom: 16,
    fontSize: 14,
  },
  accuracyBadge: {
    marginLeft: "auto",
    fontSize: 12,
    color: "#666",
    backgroundColor: "#f0f0f0",
    padding: "2px 8px",
    borderRadius: 10,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontWeight: 600,
    fontSize: 14,
    color: "#555",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    boxSizing: "border-box" as const,
    outline: "none",
  },
  checklistContainer: {
    border: "1px solid #eee",
    borderRadius: 8,
    overflow: "hidden",
  },
  checklistItem: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    borderBottom: "1px solid #f0f0f0",
    cursor: "pointer",
    fontSize: 15,
  },
  checkbox: {
    width: 20,
    height: 20,
    marginRight: 12,
    accentColor: "#0cb99d",
  },
  uploadBtn: {
    padding: "6px 16px",
    backgroundColor: "#0cb99d",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  photoArea: {
    border: "2px dashed #ddd",
    borderRadius: 8,
    padding: 16,
    textAlign: "center" as const,
    cursor: "pointer",
    minHeight: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  photoPlaceholder: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 8,
    color: "#999",
    fontSize: 14,
  },
  photoPreview: {
    maxWidth: "100%",
    maxHeight: 240,
    borderRadius: 8,
  },
  submitBtn: {
    width: "100%",
    padding: "14px 0",
    backgroundColor: "#0cb99d",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
  },
  errorText: {
    color: "#dc3545",
    fontSize: 13,
    marginTop: 4,
    display: "block",
  },
  errorBox: {
    backgroundColor: "#fdecea",
    color: "#dc3545",
    border: "1px solid #f5c6cb",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 14,
  },
  warningBox: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    border: "1px solid #ffc107",
    borderRadius: 8,
    padding: "10px 14px",
    marginTop: 12,
    fontSize: 14,
  },
  successIcon: {
    fontSize: 48,
    textAlign: "center" as const,
    marginBottom: 8,
  },
  text: {
    fontSize: 15,
    color: "#666",
    textAlign: "center" as const,
    margin: "8px 0",
  },
  timestamp: {
    fontSize: 13,
    color: "#aaa",
    textAlign: "center" as const,
    marginTop: 16,
  },
};

export default CleaningLog;
